import { ComponentEntry } from './types';
import { CATALOG } from './catalog';

const VALID_ROLES = ['surface', 'section', 'primitive'] as const;

export interface CatalogValidationError {
  type: string;
  rule: string;
  message: string;
}

/**
 * Validates the `role` grammar on every catalog entry. Atomic tier is a PRIOR: an
 * organism-category entry (the catalog's only tier signal — see `types.ts`) can never
 * be `primitive`, since a primitive is display/input vocabulary composed INSIDE a
 * section, and an organism is too large a unit to compose that way.
 */
export function validateCatalog(entries: ComponentEntry[] = CATALOG): CatalogValidationError[] {
  const errors: CatalogValidationError[] = [];

  for (const entry of entries) {
    if (!entry.role) {
      errors.push({
        type: entry.type,
        rule: 'role-required',
        message: `Catalog entry "${entry.type}" is missing a role — every entry must declare role: 'surface' | 'section' | 'primitive'.`,
      });
      continue;
    }

    if (!VALID_ROLES.includes(entry.role)) {
      errors.push({
        type: entry.type,
        rule: 'role-valid',
        message: `Catalog entry "${entry.type}" has an invalid role "${entry.role}" — must be one of ${VALID_ROLES.join(', ')}.`,
      });
    }

    if (entry.category === 'organism' && entry.role === 'primitive') {
      errors.push({
        type: entry.type,
        rule: 'organism-not-primitive',
        message: `Catalog entry "${entry.type}" is organism-tier (category: "organism") and cannot have role "primitive" — organisms must be 'surface' or 'section'.`,
      });
    }

    // `internal` entries (EAINT-844 Phase F9) are structure the renderer/composer instantiates
    // itself — never a PLACEMENT pick from the rail, gallery, or the agent's `search_composables`.
    // They stay fully documented (`description`, `whenToUse`, `props`) on purpose: the two internal
    // Surface roots are still chosen through the topbar surface-swap control, so their guidance
    // must remain readable and their entries reachable (see `isKindPlaceableAnywhere`). `examples`
    // is different — it is rendered as a ready-to-use "drop this in" snippet, the one affordance
    // that would document an internal entry as placeable and contradict the flag.
    if (entry.internal && entry.examples && entry.examples.length > 0) {
      errors.push({
        type: entry.type,
        rule: 'internal-no-placement-affordances',
        message: `Catalog entry "${entry.type}" is internal and cannot declare "examples" — internal entries must never carry gallery/placement affordances.`,
      });
    }
  }

  return errors;
}
